import { apiFetch } from "../utils/apiFetch";
import React, { useCallback, useEffect, useRef, useState } from "react";
import NewClassPopup from "./NewClassPopup";
import FirstClassSetup from "./FirstClassSetup";
import ModuleProgress from "./ModuleProgress";
import ModuleActions from "./ModuleActions";
import { useNavigate, useSearchParams } from "react-router";
import { useCourse } from "../context/CourseContext";
import { useSocket } from "../context/SocketContext";
import { FiPlus, FiSearch, FiVideo, FiFileText, FiExternalLink, FiCalendar, FiTrash2, FiLoader, FiCheckCircle, FiCircle, FiFolder, FiArrowLeft, FiArrowRight, FiX, FiCheck } from "react-icons/fi";
import { groupClasses, resumeClass } from "../utils/classModules.mjs";
import LessonVideo from "./LessonVideo";
import VideoPlayer from "./VideoPlayer";
import { lessonPlayback } from "../utils/lessonPlayback.mjs";
import { useAuth } from "../context/AuthContext";
import ClassDeletePopup from "./ClassDeletePopup";
import { useQueryClient } from "@tanstack/react-query";
import { useAccountQuery, useGroupClasses } from "../utils/queries";

const Classes = () => {
  const [selectedId, setSelectedId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeModuleId = searchParams.get("module");
  useEffect(() => {
    setSelectedId(null); setQuery(""); setRescheduleIndex(null);
    setModuleEditor(null);
  }, [activeModuleId]);
  const [moduleEditor, setModuleEditor] = useState(null);
  const [moduleTitle, setModuleTitle] = useState("");
  const [moduleBusy, setModuleBusy] = useState(false);
  const [moduleError, setModuleError] = useState("");
  const moduleDialog = useRef(null);
  useEffect(() => {
    if (!moduleEditor) return;
    setModuleError("");
    const dialog = moduleDialog.current;
    const focused = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); focused?.focus(); };
  }, [moduleEditor]);
  const [newClassPopup, setNewClassPopup] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());
  const [rescheduleIndex, setRescheduleIndex] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState("");
  const [deletePopup, setDeletePopup] = useState(false);
  const [query, setQuery] = useState("");
  const [localError, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [progressErrors, setProgressErrors] = useState({});
  const [marking, setMarking] = useState({});
  const progressGeneration = useRef(0);
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { course } = useCourse();
  const { userId } = useAuth();
  const canTrackProgress = Boolean(userId) && String(course?.creatorId) !== String(userId)
    && Boolean(course?.members?.some(member => String(member.userId?._id || member.userId) === String(userId) && member.role === "member"));
  const trackingEnabled = useRef(canTrackProgress);
  trackingEnabled.current = canTrackProgress;
  const client = useQueryClient();
  const classQuery = useGroupClasses(course?._id);
  const progressQuery = useAccountQuery(["progress", course?._id], `/groups/${course?._id}/class-progress`, {
    enabled: Boolean(course?._id && canTrackProgress),
  });
  const { setClasses, setModules } = classQuery;
  const classes = classQuery.data && (!canTrackProgress || progressQuery.data) ? classQuery.data.classes : null;
  const modules = classQuery.data?.modules || [];
  const progress = canTrackProgress ? Object.fromEntries((progressQuery.data?.progress || []).map(item => [String(item.lesson), item])) : {};
  const error = localError || classQuery.error?.message || (canTrackProgress && progressQuery.error?.message) || "";
  const retryQueries = () => { setError(""); classQuery.refetch(); if (canTrackProgress) progressQuery.refetch(); };

  const isOwnerOrAdmin = course?.members.some(
    (member) =>
      String(member.userId?._id || member.userId) === String(userId) &&
      (member.role === "owner" || member.role === "admin"),
  );

  useEffect(() => {
    if (!socket) return;

    const onClassExpiration = ({ classId, status }) => {
      setClasses((prev) => {
        if (!prev) return prev;
        return prev.map((cls) => {
          if (String(cls._id) === String(classId)) {
            return {
              ...cls,
              status,
            };
          }
          return cls;
        });
      });
    };

    const onClassRescheduled = ({ classId, scheduledAt, status }) => {
      setClasses((previous) =>
        previous?.map((cls) =>
          String(cls._id) === String(classId)
            ? { ...cls, scheduledAt, status }
            : cls,
        ),
      );
    };

    const onClassDeletion = ({ classId }) => {
      setClasses((prev) =>
        prev?.filter((cls) => String(cls._id) !== String(classId)),
      );
    };

    const onClassEnding = ({ cls }) => {
      setClasses((prev) =>
        prev?.map((cl) => {
          if (String(cl._id) === String(cls._id)) return cls;
          return cl;
        }),
      );
    };

    const onRecordingReady = ({ classId, finalVideo, recordingStatus }) => {
      setClasses((previous) =>
        previous?.map((cls) =>
          String(cls._id) === String(classId)
            ? {
                ...cls,
                recording: {
                  ...cls.recording,
                  status: recordingStatus,
                  finalVideo,
                },
              }
            : cls,
        ),
      );
    };

    socket.on("class:expired", onClassExpiration);
    socket.on("class:rescheduled", onClassRescheduled);
    socket.on("class:deleted", onClassDeletion);
    socket.on("class:ended", onClassEnding);
    socket.on("class:recording-ready", onRecordingReady);
    return () => {
      socket.off("class:expired", onClassExpiration);
      socket.off("class:rescheduled", onClassRescheduled);
      socket.off("class:deleted", onClassDeletion);
      socket.off("class:ended", onClassEnding);
      socket.off("class:recording-ready", onRecordingReady);
    };
  }, [socket, setClasses]);

  useEffect(() => {
    const updateCurrentTime = () => {
      setNow(new Date());
    };
    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    progressGeneration.current++;
    setSetupOpen(false); setNewClassPopup(false); setSelectedId(null);
    setModuleEditor(null); setProgressErrors({}); setError("");
    return () => { progressGeneration.current++; };
  }, [course?._id, userId, canTrackProgress]);

  const saveLessonProgress = useCallback(async (classId, payload) => {
    if (!trackingEnabled.current) return false;
    const generation = progressGeneration.current;
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/classes/${classId}/progress`, {
        method: "POST", keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not save progress");
      const queryKey = ["account", userId, "progress", course?._id];
      await client.cancelQueries({ queryKey, exact: true });
      client.setQueryData(queryKey, previous => {
        const rows = previous?.progress || [];
        if ((rows.find(item => String(item.lesson) === classId)?.__v ?? -1) > (data.progress.__v ?? 0)) return previous;
        return { progress: [...rows.filter(item => String(item.lesson) !== classId), data.progress] };
      });
      client.invalidateQueries({ queryKey: ["account", userId, "members", course?._id] });
      if (generation === progressGeneration.current) {
        setProgressErrors(previous => ({ ...previous, [classId]: "" }));
      }
      return true;
    } catch (error) {
      if (generation === progressGeneration.current) setProgressErrors(previous => ({ ...previous, [classId]: error.message }));
      return false;
    }
  }, [client, userId, course?._id]);

  const markDone = async classId => {
    if (marking[classId]) return;
    setMarking(previous => ({ ...previous, [classId]: true }));
    await saveLessonProgress(classId, { action: "manual" });
    setMarking(previous => ({ ...previous, [classId]: false }));
  };

  const handleReschedule = async (classId) => {
    if (!rescheduleAt) {
      setError("Please pick a reschedule time.");
      return;
    }
    setBusyId(classId);
    setError("");
    try {
      const res = await apiFetch(
        `${process.env.API_URL}/api/classes/${classId}/reschedule`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",

          },
          body: JSON.stringify({
            scheduledAt: new Date(`${rescheduleAt}+05:30`).toISOString(),
          }),
        },
      );
      const data = await res.json();

      if (!res.ok) {
        throw new Error(`${res.status}: ${data.message}`);
      }

      setClasses((previous) =>
        previous.map((cls) =>
          String(cls._id) === String(classId) ? data.cls : cls,
        ),
      );
      setRescheduleIndex(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const joinLiveClass = async (classId) => {
    setBusyId(classId);
    setError("");
    try {
      const response = await apiFetch(
        `${process.env.API_URL}/api/classes/${classId}/join`,
        {
          method: "POST",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Could not join class");
      }

      navigate(`/courses/${course.slug}/classes/${classId}/live`, {
        state: {
          token: data.token,
          serverUrl: data.serverUrl,
          isTeacher: data.isTeacher,
          classRecordingStatus: data.classRecordingStatus,
          classTitle: classes?.find(cls => cls._id === classId)?.title,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const visibleClasses = classes?.filter(cls => {
    const matchesQuery = `${cls.title} ${cls.description || ""} ${(cls.attachments || []).map(att => att.name).join(" ")}`.toLowerCase().includes(query.trim().toLowerCase());
    return matchesQuery;
  });
  const visibleIds = new Set(visibleClasses?.map(cls => cls._id));
  const grouped = groupClasses(classes || [], modules);
  const activeModule = grouped.find(module => module._id === activeModuleId);
  const ordered = activeModule?.lessons || [];
  const selected = ordered.find(cls => cls._id === selectedId) || resumeClass(activeModule ? [activeModule] : [], progress);
  const matchingModules = grouped.filter(module => module.title.toLowerCase().includes(query.trim().toLowerCase()) || module.lessons.some(cls => visibleIds.has(cls._id)));
  const selectedIndex = ordered.findIndex(cls => cls._id === selected?._id);
  useEffect(() => {
    if (selected && selected._id !== selectedId) {
      setSelectedId(selected._id);
    }
  }, [selected?._id, selectedId]);
  const selectClass = cls => {
    setSelectedId(cls._id);
    setRescheduleIndex(null);
  };
  const openModule = module => {
    const params = new URLSearchParams(searchParams);
    if (module) params.set("module", module._id); else params.delete("module");
    setSearchParams(params);
    setSelectedId(null); setQuery(""); setRescheduleIndex(null);
    setModuleEditor(null); setError("");
  };
  const saveModule = async event => {
    event.preventDefault();
    if (moduleBusy) return;
    setModuleBusy(true); setModuleError("");
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/${moduleEditor === "new" ? `groups/${course._id}/modules` : `modules/${moduleEditor}`}`, {
        method: moduleEditor === "new" ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: moduleTitle }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not save module");
      setModules(previous => moduleEditor === "new" ? [...previous, data.module] : previous.map(module => module._id === data.module._id ? data.module : module));
      setModuleEditor(null);
    } catch (error) { setModuleError(error.message); }
    finally { setModuleBusy(false); }
  };

  return (
    <main className={`mx-auto px-4 py-6 sm:px-8 ${activeModule ? "max-w-5xl" : "max-w-4xl"}`}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
        <div className="flex min-w-0 items-center gap-3 sm:flex-1">{activeModuleId && <button onClick={() => openModule(null)} aria-label="All modules" title="All modules" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100"><FiArrowLeft aria-hidden="true" /></button>}<h2 id="module-heading" className="min-w-0 break-words text-xl font-semibold">{activeModule?.title || "Modules"}</h2><span className="shrink-0 text-sm text-gray-600">{classes ? activeModule ? `${ordered.length} ${ordered.length === 1 ? "class" : "classes"}` : `${grouped.length} ${grouped.length === 1 ? "module" : "modules"}` : ""}</span></div>
        {activeModule && canTrackProgress && <div className="w-full sm:ml-auto sm:w-64 sm:shrink-0"><ModuleProgress module={activeModule} progress={progress} /></div>}
        {isOwnerOrAdmin && <div className="ml-auto flex flex-wrap justify-end gap-2">
          {!activeModule && <button disabled={!classes || moduleBusy} onClick={() => { setModuleEditor("new"); setModuleTitle(""); }} className="inline-flex h-10 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-semibold disabled:opacity-50"><FiPlus aria-hidden="true" />Add module</button>}
          {(activeModule || !grouped.length) && <button disabled={!classes || moduleBusy} onClick={() => modules.length ? setNewClassPopup(true) : setSetupOpen(true)} className="inline-flex h-10 items-center gap-2 rounded-md bg-orange-700 px-3 text-sm font-semibold text-white hover:bg-orange-800 disabled:opacity-50"><FiPlus aria-hidden="true" />Add class</button>}
        </div>}
      </div>
      {error && <div role="alert" className="mb-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}{!classes && <button className="ml-3 underline" onClick={retryQueries}>Try again</button>}</div>}
      {moduleEditor && <dialog ref={moduleDialog} aria-labelledby="module-dialog-title" onCancel={event => { event.preventDefault(); if (!moduleBusy) setModuleEditor(null); }} className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-lg border border-gray-300 bg-white p-5 text-gray-950 shadow-xl backdrop:bg-black/40 sm:p-6">
        <div className="mb-5 flex items-center justify-between gap-3"><h2 id="module-dialog-title" className="text-lg font-semibold">{moduleEditor === "new" ? "Add module" : "Edit module"}</h2><button disabled={moduleBusy} type="button" onClick={() => setModuleEditor(null)} aria-label="Cancel module editing" title="Close" className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-gray-100 disabled:opacity-50"><FiX /></button></div>
        <form onSubmit={saveModule} className="space-y-4">
          <label className="block text-sm font-medium">{moduleEditor === "new" ? "New module" : "Rename module"}<input autoFocus required maxLength={120} disabled={moduleBusy} value={moduleTitle} onChange={event => setModuleTitle(event.target.value)} className="mt-2 block h-11 w-full rounded-md border border-gray-300 bg-white px-3 outline-orange-700" /></label>
          {moduleError && <p role="alert" className="text-sm text-red-700">{moduleError}</p>}
          <div className="flex justify-end"><button disabled={moduleBusy || !moduleTitle.trim()} aria-busy={moduleBusy} aria-label="Save module" className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-orange-700 px-4 text-sm font-semibold text-white hover:bg-orange-800 disabled:opacity-50">{moduleBusy ? <FiLoader className="animate-spin" /> : <FiCheck />}Save module</button></div>
        </form>
      </dialog>}
      {!classes && !error ? <div role="status" aria-label="Loading classes" className="flex min-h-64 items-center justify-center"><FiLoader className="h-6 w-6 animate-spin text-orange-700" /></div>
        : classes && !activeModule ? <section aria-labelledby="module-heading" className="max-w-3xl">
          <label className="mb-4 flex h-10 w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-3 sm:w-64"><FiSearch aria-hidden="true" className="shrink-0 text-gray-500" /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search modules" aria-label="Search modules" className="min-w-0 w-full bg-transparent text-sm outline-none" /></label>
          <ul className="space-y-3">
            {matchingModules.map(module => <li key={module._id} className="rounded-lg border border-gray-300 bg-white px-4 shadow-sm sm:px-5">
              <div className="flex items-center gap-2">
              <button onClick={() => openModule(module)} className="flex min-w-0 flex-1 items-center gap-4 py-5 text-left hover:text-orange-800">
                {!canTrackProgress && <FiFolder aria-hidden="true" className="h-6 w-6 shrink-0 text-blue-700" />}
                <span className="min-w-0 flex-1"><span className="block break-words text-base font-semibold text-gray-900">{module.title}</span><span className="mt-1 block text-sm text-gray-600">{module.lessons.length} {module.lessons.length === 1 ? "class" : "classes"}</span></span>
              </button>
              {isOwnerOrAdmin && module._id !== "general" && <ModuleActions title={module.title} disabled={moduleBusy} onEdit={() => { setModuleEditor(module._id); setModuleTitle(module.title); }} />}
              </div>
              {canTrackProgress && <div className="pb-4"><ModuleProgress module={module} progress={progress} /></div>}
            </li>)}
            {!matchingModules.length && <li className="py-10 text-sm text-gray-600">{query ? "No matching modules." : "No modules yet."}</li>}
          </ul>
        </section> : classes && <div className={`grid min-w-0 items-start gap-5 ${ordered.length ? "lg:grid-cols-[240px_minmax(0,1fr)]" : ""}`}>
          {ordered.length > 0 && <nav aria-label="Module classes" className="max-h-96 min-w-0 overflow-y-auto border-b border-gray-300 lg:sticky lg:top-36 lg:max-h-[calc(100dvh-10rem)]">
                <label className="sticky top-0 z-10 flex h-10 w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-3"><FiSearch aria-hidden="true" className="shrink-0 text-gray-500" /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search classes" aria-label="Search classes" className="min-w-0 w-full bg-transparent text-sm outline-none" /></label>
                <ul className="space-y-1 py-3">
                  {ordered.filter(cls => visibleIds.has(cls._id)).map(cls => <li key={cls._id}><button type="button" aria-current={selected?._id === cls._id ? "step" : undefined} onClick={() => selectClass(cls)} className={`flex w-full items-start gap-2 rounded-md border border-l-4 px-3 py-3 text-left text-sm shadow-sm ${selected?._id === cls._id ? "border-orange-700 bg-orange-100 font-bold text-orange-950" : "border-gray-300 border-l-transparent bg-white font-medium text-gray-700 hover:border-gray-500"}`}>
                    <span className="shrink-0 tabular-nums">{ordered.indexOf(cls) + 1}.</span>
                    <span className="min-w-0 flex-1 break-words">{cls.title}</span>
                    {canTrackProgress && progress[cls._id]?.completedAt && <FiCheckCircle aria-label="Completed" className="mt-0.5 shrink-0 text-green-700" />}
                    {cls.type === "resource" ? <FiFileText aria-hidden="true" className="mt-0.5 shrink-0" /> : <FiVideo aria-hidden="true" className="mt-0.5 shrink-0" />}
                  </button></li>)}
                </ul>
            {!ordered.some(cls => visibleIds.has(cls._id)) && Boolean(query) && <div className="py-5 text-sm text-gray-600">No matching classes.<button onClick={() => setQuery("")} className="ml-2 font-medium text-orange-700">Clear search</button></div>}
          </nav>}
          <section aria-label="Selected class" className={`w-full min-w-0 ${ordered.length ? "max-w-xl" : ""}`}>
            {!selected && <div className="py-20 text-center"><FiVideo aria-hidden="true" className="mx-auto mb-4 h-8 w-8 text-gray-500" /><h3 className="text-base font-semibold">No classes yet</h3></div>}
            {(selected ? [selected] : []).map(cls => {
              const playback = lessonPlayback(cls, process.env.API_URL);
              const videoUrl = playback.src;
              const statusStyle = cls.status === "live" ? "bg-red-50 text-red-700" : cls.status === "missed" ? "bg-amber-50 text-amber-800" : "bg-blue-50 text-blue-700";
              return <article key={cls._id} className="min-w-0 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm">
                {videoUrl && (canTrackProgress ? <LessonVideo key={videoUrl} {...playback} title={cls.title} completed={Boolean(progress[cls._id]?.completedAt)} onProgress={payload => saveLessonProgress(cls._id, payload)} />
                  : <VideoPlayer key={videoUrl} {...playback} title={cls.title} poster={cls.thumbnail} />)}
                {!videoUrl && (playback.processing || playback.failed) && <div role="status" className="flex aspect-video items-center justify-center gap-2 bg-gray-100 p-4 text-sm font-medium text-gray-700">
                  {playback.failed ? "Video processing failed. Please contact the group owner." : <><FiLoader className="animate-spin" />Video processing...</>}
                </div>}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="min-w-0 flex-1 break-words text-base font-semibold">{cls.title}</h3>
                    {canTrackProgress && progress[cls._id]?.completedAt && <button type="button" disabled className="inline-flex min-h-8 shrink-0 items-center gap-1.5 rounded-md border border-green-200 bg-green-50 px-2 text-xs font-medium text-green-800"><FiCheckCircle aria-hidden="true" />Completed</button>}
                    {isOwnerOrAdmin && <button onClick={() => setDeletePopup(cls)} aria-label={`Delete ${cls.title}`} title="Delete class" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-red-50 hover:text-red-700"><FiTrash2 aria-hidden="true" /></button>}
                  </div>
                  {cls.description && <p className="mt-2 whitespace-pre-wrap break-words text-sm font-medium leading-5 text-gray-800">{cls.description}</p>}
                  {cls.status && <span className={`mt-3 inline-block rounded px-2 py-1 text-xs font-medium capitalize ${statusStyle}`}>{cls.status}</span>}
                  {cls.scheduledAt && <p className="mt-3 flex items-start gap-2 text-xs leading-5 text-gray-600"><FiCalendar aria-hidden="true" className="mt-0.5 shrink-0" /><time dateTime={cls.scheduledAt}>{new Date(cls.scheduledAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} IST</time></p>}
                  {cls.recording?.status === "merging" && !videoUrl && <p role="status" className="mt-4 flex items-center gap-2 text-sm text-gray-600"><FiLoader className="animate-spin" />Processing recording...</p>}
                  {cls.attachments?.length > 0 && <div className="mt-4 border-t border-gray-100 pt-3">
                    <h4 className="mb-2 text-xs font-semibold uppercase text-gray-600">Documents</h4>
                    <ul className="divide-y divide-gray-100">{cls.attachments.map((att, index) => <li key={att._id || index}>
                        <a href={`${process.env.API_URL}/api/classes/${cls._id}/documents/${att._id}`} target="_blank" rel="noopener noreferrer"
                        onClick={() => saveLessonProgress(cls._id, { action: "document", attachmentId: String(att._id) })}
                        onAuxClick={event => { if (event.button === 1) saveLessonProgress(cls._id, { action: "document", attachmentId: String(att._id) }); }}
                        className="flex min-h-10 items-center gap-2 py-2 text-sm font-medium text-gray-800 hover:text-orange-700">
                        <FiFileText aria-hidden="true" className="h-5 w-5 shrink-0 text-blue-600" /><span className="min-w-0 flex-1 break-words">{att.name || "Document"}</span><FiExternalLink aria-hidden="true" className="h-4 w-4 shrink-0" />
                      </a>
                      {canTrackProgress && progress[cls._id]?.openedDocuments?.includes(String(att._id)) && <span className="mb-1 inline-flex items-center gap-1 text-xs text-green-700"><FiCheckCircle aria-hidden="true" />Opened</span>}
                    </li>)}</ul>
                  </div>}
                  {cls.status && cls.status !== "ended" && <div className="mt-4 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
                    <button disabled={Boolean(busyId) || now.getTime() < new Date(cls.scheduledAt).getTime() - 5 * 60 * 1000 || cls.status === "missed"} onClick={() => joinLiveClass(cls._id)}
                      className="flex h-9 items-center gap-2 rounded-md bg-orange-700 px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"><FiVideo aria-hidden="true" />{busyId === cls._id ? "Please wait..." : "Join class"}</button>
                    {isOwnerOrAdmin && ["missed", "scheduled"].includes(cls.status) && <button onClick={() => { setRescheduleIndex(rescheduleIndex === cls._id ? null : cls._id); setRescheduleAt(""); }} className="flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-medium"><FiCalendar aria-hidden="true" />Reschedule</button>}
                  </div>}
                  {rescheduleIndex === cls._id && <form onSubmit={event => { event.preventDefault(); handleReschedule(cls._id); }} className="mt-4 flex flex-wrap items-end gap-3">
                    <label className="min-w-0 flex-1 text-xs text-gray-600">New date and time (IST)<input required type="datetime-local" value={rescheduleAt} onChange={event => setRescheduleAt(event.target.value)} className="mt-1 block h-10 w-full min-w-0 rounded-md border border-gray-300 px-2 text-sm text-gray-900" /></label>
                    <button disabled={Boolean(busyId)} className="h-10 rounded-md bg-gray-900 px-3 text-sm font-medium text-white disabled:opacity-50">Save</button>
                  </form>}
                  {canTrackProgress && !progress[cls._id]?.completedAt && <div className="mt-4 border-t border-gray-100 pt-3">
                    <button type="button" disabled={Boolean(progress[cls._id]?.completedAt) || marking[cls._id]} aria-busy={Boolean(marking[cls._id])}
                      onClick={() => markDone(cls._id)} className={`inline-flex min-h-9 items-center gap-2 rounded-md border px-3 text-sm font-medium ${progress[cls._id]?.completedAt ? "border-green-200 bg-green-50 text-green-800" : "border-gray-300 text-gray-600 hover:bg-gray-50 disabled:cursor-wait"}`}>
                      {progress[cls._id]?.completedAt ? <FiCheckCircle aria-hidden="true" /> : <FiCircle aria-hidden="true" />}{progress[cls._id]?.completedAt ? "Completed" : "Mark as done"}
                    </button>
                    {progressErrors[cls._id] && <p role="alert" className="mt-2 text-xs text-red-700">{progressErrors[cls._id]}</p>}
                  </div>}
                </div>
              </article>;
            })}
            {selected && <div className="mt-4 flex items-center justify-between gap-3">
              <button disabled={selectedIndex <= 0} onClick={() => selectClass(ordered[selectedIndex - 1])} className="inline-flex min-h-10 items-center gap-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:text-gray-400"><FiArrowLeft aria-hidden="true" />Previous</button>
              <span className="text-xs text-gray-600">{selectedIndex + 1} / {ordered.length}</span>
              <button disabled={selectedIndex >= ordered.length - 1} onClick={() => selectClass(ordered[selectedIndex + 1])} className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-orange-800 disabled:cursor-not-allowed disabled:text-gray-400">Next class<FiArrowRight aria-hidden="true" /></button>
            </div>}
          </section>
          </div>}
      {deletePopup && <ClassDeletePopup setDeletePopup={setDeletePopup} classTitle={deletePopup.title} classId={deletePopup._id} setClasses={setClasses} />}
      {newClassPopup && <NewClassPopup setNewClassPopup={setNewClassPopup} workspace={course} setClasses={setClasses} modules={modules} initialModuleId={activeModule?._id || modules[0]?._id || ""} />}
      {setupOpen && <FirstClassSetup workspace={course} setClasses={setClasses} onClose={() => setSetupOpen(false)} onModuleCreated={module => setModules(previous => [...previous, module])} onFinish={(module, cls) => { setSetupOpen(false); openModule(module); setSelectedId(cls._id); }} />}
    </main>
  );
};

export default Classes;
